import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PointsProvider } from './context/PointsContext';
import { CountryProvider } from './context/CountryContext';
import { LanguageProvider } from './context/LanguageContext';
import { TourIntegrationProvider } from './components/onboarding/TourIntegrationProvider';


// Import Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';

// Import global styles
import './styles/global.css';
import './App.css';

// Import page components
import HomePage from './pages/HomePage';
import TaskTakerPage from './pages/tasks/TaskTakerPage';
import PublisherPage from './pages/tasks/PublisherPage';
import TaskDetailPage from './pages/tasks/TaskDetailPage';
import CreateTaskPage from './pages/tasks/CreateTaskPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import AuthCallback from './pages/auth/AuthCallback';
import SettingsPage from './pages/SettingsPage';
import ClaimBonusesPage from './pages/points/ClaimBonusesPage';
import PointsHistoryPage from './pages/points/PointsHistoryPage';
import CreateQuestionnairePage from './pages/questionnaires/CreateQuestionnairePage';
import QuestionnairePublisherPage from './pages/questionnaires/QuestionnairePublisherPage';
import QuestionnaireTakerPage from './pages/questionnaires/QuestionnaireTakerPage';
import TakeQuestionnairePage from './pages/questionnaires/TakeQuestionnairePage';
import QuestionnaireResultsPage from './pages/questionnaires/QuestionnaireResultsPage';

import JoinTeamPage from './pages/teams/JoinTeamPage';
import SetupTeamPage from './pages/teams/SetupTeamPage';
import CreateTeamRequestPage from './pages/teams/CreateTeamRequestPage';
import HelpPage from './pages/HelpPage';
import ContactPage from './pages/ContactPage';
import ForumsHomePage from './pages/forum/ForumsHomePage';
import ForumCategoryPage from './pages/forum/ForumCategoryPage';
import ForumsTopicPage from './pages/forum/ForumsTopicPage';
import CreatePostPage from './pages/forum/CreatePostPage';
import CreateTopicPage from './pages/forum/CreateTopicPage';
import ModerationDashboard from './pages/ModerationDashboard';
import { PreReleaseProjectsPage } from './pages/projects/PreReleaseProjectsPage';
import { PreReleaseProjectDetailPage } from './pages/projects/PreReleaseProjectDetailPage';
import { PublisherDashboardPage } from './pages/projects/PublisherDashboardPage';
import CreatePreReleaseProjectPage from './pages/projects/CreatePreReleaseProjectPage';
import UnsubscribePage from './pages/notifications/UnsubscribePage';
import NotificationAnalyticsPage from './pages/notifications/NotificationAnalyticsPage';
import ContactHistoryPage from './pages/notifications/ContactHistoryPage';
import MessagesPageEmail from './pages/messages/MessagesPageEmail';
import StartupJourneySetupPage from './pages/journey/StartupJourneySetupPage';
import JourneyListPage from './pages/journey/JourneyListPage';
import { WorkflowDisplayPage } from './pages/journey/WorkflowDisplayPage';
import AIIdeaGeneratorPage from './pages/journey/AIIdeaGeneratorPage';
import LaunchAssetsGeneratorPage from './pages/journey/LaunchAssetsGeneratorPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';

// Import layout components
import TopNavbar from './components/navigation/TopNavbar';
import SideNavbar from './components/navigation/SideNavbar';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProjectCreationGuard from './components/projects/ProjectCreationGuard';
import BonusNotification from './components/points/BonusNotification';

/**
 * Component to detect auth tokens in URL hash and redirect appropriately
 * Handles magic links and password reset links that land on any page
 */
const AuthHashDetector: React.FC = () => {
  useEffect(() => {
    const hash = window.location.hash;

    // Check if we have Supabase auth tokens in the hash
    if (hash && (hash.includes('access_token') || hash.includes('refresh_token'))) {
      console.log('AuthHashDetector: Auth tokens detected in URL hash');

      // Determine if this is a password reset or magic link
      const hashParams = new URLSearchParams(hash.substring(1));
      const type = hashParams.get('type');

      if (type === 'recovery') {
        console.log('AuthHashDetector: Password recovery detected, redirecting to /reset-password');
        // Password reset - redirect to reset password page (preserving hash)
        window.location.href = `/reset-password${hash}`;
      } else if (type === 'magiclink' || hashParams.get('access_token')) {
        console.log('AuthHashDetector: Magic link or auth callback detected, redirecting to /auth/callback');
        // Magic link or other auth - redirect to auth callback (preserving hash)
        window.location.href = `/auth/callback${hash}`;
      }
    }
  }, []);

  return null;
};

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Initialize based on screen size
    return window.innerWidth <= 768;
  });
  const [userToggledSidebar, setUserToggledSidebar] = useState(false);
  const toggleInProgressRef = useRef(false);
  const lastToggleTimeRef = useRef(0);

  // Only auto-collapse on significant resize changes, not on manual toggles
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;

    const checkScreenSize = () => {
      // Debounce resize events
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const isSmallScreen = window.innerWidth <= 768;

        // Only auto-collapse if transitioning from large to small screen
        // and user hasn't manually toggled recently
        if (isSmallScreen && !userToggledSidebar) {
          setSidebarCollapsed(true);
        }

        // Reset user toggle flag after resize settles
        if (userToggledSidebar) {
          setTimeout(() => setUserToggledSidebar(false), 1000);
        }
      }, 150);
    };

    window.addEventListener('resize', checkScreenSize);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [userToggledSidebar]);

  const toggleSidebar = () => {
    const now = Date.now();
    const timeSinceLastToggle = now - lastToggleTimeRef.current;
    
    // Prevent multiple toggles within 600ms
    if (toggleInProgressRef.current || timeSinceLastToggle < 600) {
      console.log(`Toggle blocked: inProgress=${toggleInProgressRef.current}, timeSince=${timeSinceLastToggle}ms`);
      return;
    }

    const timestamp = new Date().toISOString();
    const stackTrace = new Error().stack;
    console.log(`[${timestamp}] Toggle sidebar called, current state:`, sidebarCollapsed);
    console.log('Stack trace:', stackTrace);
    
    toggleInProgressRef.current = true;
    lastToggleTimeRef.current = now;
    setUserToggledSidebar(true);
    
    setSidebarCollapsed((prev) => {
      console.log(`[${timestamp}] Setting sidebar to:`, !prev);
      return !prev;
    });

    // Reset the toggle lock after animation completes
    setTimeout(() => {
      toggleInProgressRef.current = false;
      console.log('Toggle lock released');
    }, 600);
  };

  // Handle overlay click on mobile
  const handleOverlayClick = () => {
    console.log('Overlay clicked');
    setSidebarCollapsed(true);
  };

  return (
    <>
      <LanguageProvider>
        <AuthProvider>
          <PointsProvider>
            <CountryProvider>
              {/* TourIntegrationProvider disabled to prevent unnecessary tour API calls */}
              {/* <TourIntegrationProvider> */}
              <AuthHashDetector />
              <Router>
                <TopNavbar onSidebarToggle={toggleSidebar} />
                <SideNavbar
                  isCollapsed={sidebarCollapsed}
                  onOverlayClick={handleOverlayClick}
                  onToggle={toggleSidebar}
                />
                <div className={`app-container ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/tasks" element={<Navigate to="/tasks/task-taker" />} />
                    <Route path="/tasks/task-taker" element={<TaskTakerPage />} />
                    <Route path="/tasks/publisher" element={<PublisherPage />} />
                    <Route path="/create-task" element={<CreateTaskPage />} />
                    <Route path="/create-team-request" element={
                      <ProtectedRoute requireAdmin={false}>
                        <CreateTeamRequestPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
                    <Route path="/profile/:userId" element={<ProfilePage />} />
                    <Route path="/profile" element={<Navigate to="/profile/me" />} />
                    <Route path="/claim-bonuses" element={
                      <ProtectedRoute requireAdmin={false}>
                        <ClaimBonusesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/points-history" element={
                      <ProtectedRoute requireAdmin={false}>
                        <PointsHistoryPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                      <ProtectedRoute requireAdmin={true}>
                        <SettingsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/create-questionnaire" element={
                      <ProtectedRoute requireAdmin={false}>
                        <CreateQuestionnairePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/questionnaires" element={
                      <ProtectedRoute requireAdmin={false}>
                        <QuestionnaireTakerPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/questionnaires/publisher" element={
                      <ProtectedRoute requireAdmin={false}>
                        <QuestionnairePublisherPage />
                      </ProtectedRoute>
                    } />                  <Route path="/teams" element={
                      <Navigate to="/teams/join" replace />
                    } />
                    <Route path="/teams/join" element={
                      <ProtectedRoute requireAdmin={false}>
                        <JoinTeamPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/teams/setup" element={
                      <ProtectedRoute requireAdmin={false}>
                        <SetupTeamPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/join-team" element={
                      <ProtectedRoute requireAdmin={false}>
                        <JoinTeamPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/setup-team" element={
                      <ProtectedRoute requireAdmin={false}>
                        <SetupTeamPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/create-team-request" element={
                      <ProtectedRoute requireAdmin={false}>
                        <CreateTeamRequestPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/questionnaires/:questionnaireId/take" element={
                      <ProtectedRoute requireAdmin={false}>
                        <TakeQuestionnairePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/questionnaires/:questionnaireId/results" element={
                      <ProtectedRoute requireAdmin={false}>
                        <QuestionnaireResultsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/questionnaires/:questionnaireId/edit" element={
                      <ProtectedRoute requireAdmin={false}>
                        <CreateQuestionnairePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/help" element={<HelpPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/messages" element={
                      <ProtectedRoute requireAdmin={false}>
                        <MessagesPageEmail />
                      </ProtectedRoute>
                    } />
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />
                    <Route path="/forums" element={<ForumsHomePage />} />
                    <Route path="/forums/create-topic" element={<CreateTopicPage />} />
                    <Route path="/forums/category/:categorySlug" element={<ForumCategoryPage />} />
                    <Route path="/forums/category/:categorySlug/create-topic" element={<CreateTopicPage />} />
                    <Route path="/forums/topic/:topicId" element={<ForumsTopicPage />} />
                    <Route path="/forums/category/:categorySlug/topic/:topicId" element={<ForumsTopicPage />} />
                    <Route path="/forums/category/:categorySlug/create" element={<CreatePostPage />} />
                    <Route path="/forums/category/:categorySlug/topic/:topicId/reply" element={<CreatePostPage />} />
                    <Route path="/forums/category/:categorySlug/topic/:topicId/posts/:postId/edit" element={<CreatePostPage mode="edit" />} />
                    <Route path="/pre-release" element={<PreReleaseProjectsPage />} />
                    <Route path="/pre-release/:id" element={<PreReleaseProjectDetailPage />} />
                    <Route path="/pre-release/:projectId/analytics" element={
                      <ProtectedRoute requireAdmin={false}>
                        <NotificationAnalyticsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/pre-release/:projectId/contact-history" element={
                      <ProtectedRoute requireAdmin={false}>
                        <ContactHistoryPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/create-pre-release-project" element={
                      <ProtectedRoute requireAdmin={false}>
                        <ProjectCreationGuard>
                          <CreatePreReleaseProjectPage />
                        </ProjectCreationGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="/edit-pre-release-project/:id" element={
                      <ProtectedRoute requireAdmin={false}>
                        <ProjectCreationGuard>
                          <CreatePreReleaseProjectPage />
                        </ProjectCreationGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="/publisher-dashboard" element={
                      <ProtectedRoute requireAdmin={false}>
                        <PublisherDashboardPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/moderation" element={
                      <ProtectedRoute requireAdmin={true}>
                        <ModerationDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/startup-journey/setup" element={
                      <ProtectedRoute requireAdmin={false}>
                        <StartupJourneySetupPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/journey-list" element={
                      <ProtectedRoute requireAdmin={false}>
                        <JourneyListPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/journey/:journeyId" element={
                      <ProtectedRoute requireAdmin={false}>
                        <WorkflowDisplayPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/journey/ai-idea-generator" element={
                      <ProtectedRoute requireAdmin={false}>
                        <AIIdeaGeneratorPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/journey/launch-assets-generator" element={
                      <ProtectedRoute requireAdmin={false}>
                        <LaunchAssetsGeneratorPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  <BonusNotification />
                </div>
              </Router>
              {/* </TourIntegrationProvider> */}
            </CountryProvider>
          </PointsProvider>
        </AuthProvider>
      </LanguageProvider >
    </>
  );
}

export default App;
